import { useRef, useState } from "react";
import { setSplitRatio } from "@/lib/splitScreen";
import { updateMyPreferences } from "@/lib/api";
import { useAuth } from "@/auth/AuthContext";

const MIN = 25;
const MAX = 75;
const clamp = (v) => Math.min(MAX, Math.max(MIN, Math.round(v)));

// Draggable divider between split panes; the dropped width is saved as the user's default split.
export function SplitDivider({ ratio }) {
  const { refresh } = useAuth();
  const [dragging, setDragging] = useState(false);
  const last = useRef(ratio);

  const persist = async (value) => {
    setSplitRatio(value);
    try { await updateMyPreferences({ split_ratio: value }); await refresh(); } catch { /* live width still applies */ }
  };

  const onPointerDown = (e) => {
    e.preventDefault();
    const row = e.currentTarget.parentElement.getBoundingClientRect();
    const start = String(ratio);
    last.current = start;
    setDragging(true);
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
    const move = (ev) => {
      const next = String(clamp(((ev.clientX - row.left) / row.width) * 100));
      if (next !== last.current) { last.current = next; setSplitRatio(next); }
    };
    const up = () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
      setDragging(false);
      if (last.current !== start) persist(last.current);
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  };

  return (
    <div
      role="separator"
      aria-orientation="vertical"
      aria-valuenow={Number(ratio)}
      aria-valuemin={MIN}
      aria-valuemax={MAX}
      title="Drag to resize · double-click to reset to 50/50"
      onPointerDown={onPointerDown}
      onDoubleClick={() => String(ratio) !== "50" && persist("50")}
      className="group absolute top-0 bottom-0 z-30 hidden md:flex w-3 -translate-x-1/2 cursor-col-resize items-center justify-center touch-none"
      style={{ left: `${ratio}%` }}
      data-testid="split-divider"
      data-dragging={dragging ? "true" : "false"}
    >
      <div className={`h-full w-px transition-colors duration-150 ${dragging ? "bg-brand" : "bg-slate-200 dark:bg-slate-800 group-hover:bg-brand/70"}`} />
      <div className={`absolute top-1/2 -translate-y-1/2 h-10 w-1.5 rounded-full transition-[background-color,transform] duration-150 ${dragging ? "bg-brand scale-y-110" : "bg-slate-300 dark:bg-slate-600 group-hover:bg-brand"}`} data-testid="split-divider-grip" />
    </div>
  );
}
